import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supplierSchema, type SupplierFormData } from './supplier-schema';
import { useCreateSupplier, useUpdateSupplier, useNextSupplierCode } from '@/hooks/use-suppliers';
import type { Supplier } from '@erp/data-access/interfaces';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@erp/ui';
import { Button } from '@erp/ui';
import { Input } from '@erp/ui';
import { Label } from '@erp/ui';
import { Textarea } from '@erp/ui';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@erp/ui';

interface SupplierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier | null;
}

export function SupplierFormDialog({ open, onOpenChange, supplier }: SupplierFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!supplier;
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const { data: nextCode } = useNextSupplierCode(open && !isEdit);
  const [autoCode, setAutoCode] = useState<string | null>(null);

  const {
    register, handleSubmit, reset, setValue, watch,
    formState: { errors, isSubmitting },
  } = useForm<SupplierFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(supplierSchema) as any,
    defaultValues: {
      code: '', name: '', type: 'company', taxId: '', address: '',
      city: '', phone: '', email: '', notes: '',
    },
  });

  useEffect(() => {
    if (supplier) {
      reset({
        code: supplier.code, name: supplier.name, type: supplier.type,
        taxId: supplier.taxId ?? '', address: supplier.address ?? '',
        city: supplier.city ?? '', phone: supplier.phone ?? '',
        email: supplier.email ?? '', notes: supplier.notes ?? '',
      });
      setAutoCode(null);
    } else {
      reset({
        code: nextCode ?? '', name: '', type: 'company', taxId: '', address: '',
        city: '', phone: '', email: '', notes: '',
      });
      setAutoCode(nextCode ?? null);
    }
  }, [supplier, reset, open, nextCode]);

  const onSubmit = async (data: SupplierFormData) => {
    const codeUnchanged = !isEdit && autoCode !== null && data.code === autoCode;
    const input = {
      ...data,
      code: codeUnchanged ? undefined : data.code,
      taxId: data.taxId || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      phone: data.phone || undefined,
      email: data.email || undefined,
      notes: data.notes || undefined,
    };
    if (isEdit && supplier) {
      await updateMutation.mutateAsync({ id: supplier.id, input });
    } else {
      await createMutation.mutateAsync(input);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('suppliers.code')} *</Label>
              <Input id="code" {...register('code')} />
              {errors.code && <p className="text-xs text-destructive">{t(errors.code.message!)}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('suppliers.type')} *</Label>
              <Select value={watch('type')} onValueChange={(v) => setValue('type', v as 'company' | 'individual')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">{t('suppliers.typeCompany')}</SelectItem>
                  <SelectItem value="individual">{t('suppliers.typeIndividual')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('suppliers.name')} *</Label>
              <Input id="name" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{t(errors.name.message!)}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="taxId">{t('suppliers.taxId')}</Label>
              <Input id="taxId" {...register('taxId')} placeholder="0000000/A/M/000" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="address">{t('suppliers.address')}</Label>
              <Input id="address" {...register('address')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">{t('suppliers.city')}</Label>
              <Input id="city" {...register('city')} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('suppliers.phone')}</Label>
              <Input id="phone" {...register('phone')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('suppliers.email')}</Label>
              <Input id="email" type="email" {...register('email')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">{t('suppliers.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
